import type { Ref } from 'react';
import { Button, Menu } from '@kula-ai/components';
import { DotsVertical } from '@kula-ai/icons-core';
import { ActivityTooltip } from './ActivityTooltip';
import { toMenuItems, type ActivityMenuItem } from './activityMenuItem';

export type { ActivityMenuItem } from './activityMenuItem';

interface ActivityMoreMenuProps {
  items: ActivityMenuItem[];
  /**
   * Show the trigger, refuse to open it. NOT the same as passing no items: the
   * actions exist and the user is told they exist, they simply cannot be reached
   * yet (survey, with no candidate email). Pair with `disabledReason`.
   */
  disabled?: boolean;
  /** Why it will not open. Requires `disabled`. */
  disabledReason?: string;
  /**
   * Ref to the trigger button. Some follow-ups anchor to it and dereference
   * `.current` on render with no null guard (`MarkingInterviewAsDoneModal` calls
   * `getBoundingClientRect()` on its first line), so an unattached ref crashes the
   * whole drawer. Attach it here to give those modals a live node to measure.
   */
  triggerRef?: Ref<HTMLButtonElement>;
  'data-test-id'?: string;
}

/**
 * The one "More" menu, per the new design (Figma 7610:21646): an icon-only
 * `dots-vertical` trigger in a bordered button, not the old text "More ▾" link.
 *
 * It replaces ~14 hand-rolled Chakra menus across the trigger surface. Those had
 * drifted apart cosmetically — `gap="2px"` here, `gap="3px"` there, a `borderRadius`
 * on one `MenuList` and not the next — which is precisely the inconsistency this
 * redesign exists to remove. Callers now supply *what* the actions are; the kit
 * decides how they look.
 */
export const ActivityMoreMenu = ({
  items,
  disabled = false,
  disabledReason,
  triggerRef,
  'data-test-id': testId,
}: ActivityMoreMenuProps) => {
  const visibleItems = items.filter((item) => !item.hidden);
  if (visibleItems.length === 0) return null;

  const trigger = (
    <Button
      // White + secondary-100 border, per Figma. `secondary` is an INDIGO outline.
      variant="quaternary"
      size="icon"
      // Radix hands focus back to the trigger on close, so kula's branded ring
      // outlived the menu and sat on the ⋮ until something else was clicked.
      // kula's `cn` is tailwind-merge, so these DROP its `focus-visible:ring-2`
      // rather than fight it — its stylesheet loads after ours and would win.
      className="focus-visible:ring-0 focus-visible:ring-offset-0"
      aria-label="More actions"
      disabled={disabled}
      data-test-id={testId}
      ref={triggerRef}
    >
      <DotsVertical size={18} />
    </Button>
  );

  // Deliberately no `<Menu>` at all when disabled — not a `<Menu>` with a dead
  // trigger. An unreachable action must not be in the document: legacy leaves its
  // Chakra `MenuList` mounted-but-hidden behind a disabled trigger, which is how a
  // row can *look* like it offers Edit and Cancel while offering neither.
  if (disabled) {
    return (
      <ActivityTooltip
        label={disabledReason}
        data-test-id="activity-disabled-reason"
      >
        {trigger}
      </ActivityTooltip>
    );
  }

  return (
    // `align="end"` — the ⋮ sits at the right edge of the card, and kula's `Menu`
    // defaults to `align="start"`, which hangs the panel off the trigger's LEFT edge
    // and opens rightwards. There is nothing to the right, so Radix (whose
    // `collisionPadding` is 0 and which kula does not expose) shunts it back just far
    // enough to fit and it ends up flush against the viewport, touching the edge.
    // Aligning to the trigger's right edge means it opens leftwards, into the card,
    // and never collides in the first place.
    <Menu trigger={trigger} align="end" items={toMenuItems(visibleItems)} />
  );
};
